import type { ClientDetailPass } from '@/types';
import { formatDateForDisplay, getCalendarDateProgress } from '@/shared/lib/calendarDate';
import { EXPIRY_WARNING_DAYS, getPassMeta, getPassTone } from '@/shared/lib/client';
import { addS } from '@/shared/lib/addS';
import { toneColor, type PillTone } from '@/shared/ui';
import { SHORT_DATE } from '../../model/formatStartsIn';

const INFINITY_GLYPH = '∞';
const LOW_CREDITS_RATIO = 0.2;

export interface PassCardMetric {
  headline: string;
  headlineSuffix: string;
  progressLabel: string;
  remainingPercent: number | null;
}

export interface PassCardModel {
  time: PassCardMetric;
  visits: PassCardMetric;
  dateLabel: string;
  statusLabel: string;
  statusTone: PillTone;
  timeAccentColor: string | undefined;
  visitsAccentColor: string | undefined;
}

const buildTimeMetric = (pass: ClientDetailPass): PassCardMetric => {
  if (!pass.endsAt) {
    return {
      headline: INFINITY_GLYPH,
      headlineSuffix: 'days left',
      progressLabel: 'No expiry',
      remainingPercent: null,
    };
  }

  const progress = getCalendarDateProgress(pass.startsAt, pass.endsAt);
  if (!progress) {
    return {
      headline: '0',
      headlineSuffix: 'days left',
      progressLabel: '',
      remainingPercent: 0,
    };
  }

  return {
    headline: String(progress.daysLeft),
    headlineSuffix: `${addS(progress.daysLeft, 'day')} left`,
    progressLabel: `of ${progress.daysTotal} total`,
    remainingPercent: progress.percentRemaining,
  };
};

const buildVisitsMetric = (pass: ClientDetailPass): PassCardMetric => {
  const credits = pass.credits;
  if (!credits) {
    return {
      headline: INFINITY_GLYPH,
      headlineSuffix: 'visits left',
      progressLabel: 'No limit',
      remainingPercent: null,
    };
  }

  const remaining = credits.remainingCredits;
  const max = credits.maxCredits;
  const remainingPercent = max > 0 ? Math.min(Math.max((remaining / max) * 100, 0), 100) : 0;

  return {
    headline: String(remaining),
    headlineSuffix: `${addS(remaining, 'visit')} left`,
    progressLabel: `of ${max} total`,
    remainingPercent,
  };
};

const formatDateLabel = (pass: ClientDetailPass): string => {
  const start = formatDateForDisplay(pass.startsAt, undefined, SHORT_DATE);
  if (!pass.endsAt) return `Started ${start}`;
  const end = formatDateForDisplay(pass.endsAt, undefined, SHORT_DATE);
  return `${start} - ${end}`;
};

const buildStatusLabel = (daysUntilExpiry: number | null): string => {
  if (daysUntilExpiry === null) return 'Active';
  if (daysUntilExpiry === 0) return 'Expires today';
  if (daysUntilExpiry === 1) return 'Expires tomorrow';
  return `Expires in ${daysUntilExpiry} ${addS(daysUntilExpiry, 'day')}`;
};

const buildTimeAccent = (pass: ClientDetailPass): string | undefined => {
  const meta = getPassMeta(pass);
  if (!meta || meta.isFuture) return undefined;
  if (meta.isExpired) return toneColor('destructive');
  if (meta.daysUntilExpiry !== null && meta.daysUntilExpiry <= EXPIRY_WARNING_DAYS) {
    return toneColor('warning');
  }
  return undefined;
};

const buildVisitsAccent = (pass: ClientDetailPass): string | undefined => {
  const credits = pass.credits;
  if (!credits) return undefined;
  if (credits.remainingCredits === 0) return toneColor('destructive');
  if (credits.maxCredits > 0 && credits.remainingCredits / credits.maxCredits < LOW_CREDITS_RATIO) {
    return toneColor('warning');
  }
  return undefined;
};

export const getPassCardModel = (pass: ClientDetailPass): PassCardModel => {
  const meta = getPassMeta(pass);
  const tone = getPassTone(meta);
  const daysUntilExpiry = meta?.isEndingSoon ? (meta.daysUntilExpiry ?? null) : null;

  return {
    time: buildTimeMetric(pass),
    visits: buildVisitsMetric(pass),
    dateLabel: formatDateLabel(pass),
    statusLabel: buildStatusLabel(daysUntilExpiry),
    statusTone: tone,
    timeAccentColor: buildTimeAccent(pass),
    visitsAccentColor: buildVisitsAccent(pass),
  };
};
